/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * Network sender for the browser log relay (LGTM PR-4 wiring).
 *
 * Builds the `BatchSender` that POSTs batched browser logs to the backend
 * `/logs/batch`. Uses `msalInstance` directly (not the authenticated API
 * client) on purpose: the API client logs heavily via `logger`, which would
 * re-enter the relay — this path stays logging-free to avoid recursion.
 *
 * Silent-only auth: if no signed-in account or the token can't be acquired
 * silently, the batch is dropped (logging must never trigger an interactive
 * login or break the app).
 */

import { apiScopes, msalInstance } from "@/lib/msalInstance";
import type { BatchSender, RelayEvent } from "@/lib/logRelay";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8000/api/v1";

function getCsrfToken(): string | null {
  for (const cookie of document.cookie.split(";")) {
    const trimmed = cookie.trim();
    // Split on the FIRST "=" only — the value may itself contain "=" (base64
    // padding is common in CSRF tokens). split("=") would truncate it, sending a
    // wrong token -> 403 on the relay POST -> logs silently never ship in PROD.
    const eq = trimmed.indexOf("=");
    if (eq === -1) continue;
    if (trimmed.slice(0, eq) === "csrf_token") {
      return decodeURIComponent(trimmed.slice(eq + 1));
    }
  }
  return null;
}

export function createLogRelaySender(): BatchSender {
  return async (events: RelayEvent[]): Promise<void> => {
    const account = msalInstance.getActiveAccount();
    if (!account) return; // not signed in — nothing to ship

    let token: string;
    try {
      const result = await msalInstance.acquireTokenSilent({
        scopes: apiScopes.scopes,
        account,
      });
      token = result.accessToken;
    } catch {
      return; // can't auth silently; drop rather than prompt from a log flush
    }

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    const csrf = getCsrfToken();
    if (csrf) headers["X-CSRF-Token"] = csrf;

    await fetch(`${API_BASE}/logs/batch`, {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify({ events }),
      keepalive: true, // let the beforeunload flush complete
    });
  };
}
